import type {
  BillingItemType,
  ParsedBillingItem,
  ParsedCartItem,
  ParsedCartItemGroup,
  ParsedCartDTO,
} from 'ui/types/cartTypes';
import type { Product } from 'services/generated/commerce-service/models/Product';
import { getPriceToAddOneFamilyMember } from 'lib/utils/familyHelpers';
import type {
  Currency,
  Language,
  Region,
} from '@whoop/i18n/types/internationalization';
import { i18n } from '@whoop/i18n/lang/client';
import { formatDisplayPrice as formatDisplayPriceWithSiteValues } from 'ui/utils/formatHelpers';
import {
  ANNUAL_MEMBERSHIP_KEY,
  ANNUAL_MEMBERSHIP_SKU,
  TWENTYFOUR_MONTH_MEMBERSHIP_KEY,
} from 'ui/utils/constants/membershipConstants';
import {
  FAMILY_MEMBERSHIP_SKUS,
  MAX_FAMILY_SIZE,
} from 'ui/utils/constants/familyConstants';
import { shouldExcludeRenewalCopy } from '@whoop/i18n';
import { GEN_4_DEVICE_SKU } from 'ui/utils/constants/productConstants';
import { PlanTypes } from 'ui/utils/constants/maverick';
import type {
  MembershipTier,
  MembershipTierWithTrial,
} from '@/lib/constants/maverick';
import {
  ProductType,
  DeviceGeneration,
  VARIANT_ATTRIBUTE_NAMES,
  TRIAL,
} from '@/lib/constants/maverick';
import { getIsMembershipTypeInCart } from '@/lib/utils/membershipHelper';

export function getCartItemsFromCartItemGroups(
  cartItemGroups: ParsedCartItemGroup[],
): ParsedCartItem[] {
  return cartItemGroups.flatMap((group) => group.cartItems);
}

export function isMembershipAndDeviceInCartItems(
  cartItemGroups: ParsedCartItemGroup[],
): boolean {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);
  return (
    cartItems.some(
      (item) => (item.productType as ProductType) === ProductType.DEVICE,
    ) &&
    cartItems.some(
      (item) => (item.productType as ProductType) === ProductType.MEMBERSHIP,
    )
  );
}

export function hasProductTypeInCartItemGroups(
  cartItemGroups: ParsedCartItemGroup[],
  productType: ProductType,
): boolean {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);
  return Boolean(
    cartItems.some((item) => (item.productType as ProductType) === productType),
  );
}

export function getMembershipTierFromCart(
  cartItemGroups: ParsedCartItemGroup[],
): MembershipTierWithTrial | undefined {
  // handle trials (JF only): harvard devices do not have membership-tier-type in CT
  const isTrial = getIsMembershipTypeInCart(cartItemGroups, PlanTypes.TRIAL);
  if (isTrial) {
    return TRIAL;
  }

  // all other cases: get tier from device
  const device = getCartItemsFromCartItemGroups(cartItemGroups).find(
    (item) => (item.productType as ProductType) === ProductType.DEVICE,
  );
  if (!device?.attributes) return;
  const tierAttribute = device.attributes.find(
    (attr) =>
      (attr.name as VARIANT_ATTRIBUTE_NAMES) === VARIANT_ATTRIBUTE_NAMES.TIER,
  );
  if (!tierAttribute?.value?.key) return;
  return tierAttribute.value?.key as MembershipTier;
}

/**
 * @deprecated Please use hasProductTypeInCartItemGroups instead
 */
export function isUpgradeFeeInCartItems(
  cartItemGroups: ParsedCartItemGroup[],
): boolean {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);
  return cartItems.some(
    (item) => (item.productType as ProductType) === ProductType.UPGRADE_FEE,
  );
}

/**
 * @deprecated Please use hasProductTypeInCartItemGroups instead
 */
export function isExtensionInCartItems(
  cartItemGroups: ParsedCartItemGroup[],
): boolean {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);
  return cartItems.some(
    (item) => (item.productType as ProductType) === ProductType.EXTENSION,
  );
}

export function getExtensionMonthsFromCart(
  cartItemGroups: ParsedCartItemGroup[],
): number | undefined {
  const extension = getCartItemsFromCartItemGroups(cartItemGroups).find(
    (item) => (item.productType as ProductType) === ProductType.EXTENSION,
  );
  if (!extension?.attributes) return;
  const monthsAttribute = extension.attributes.find(
    (attr) =>
      (attr.name as VARIANT_ATTRIBUTE_NAMES) === VARIANT_ATTRIBUTE_NAMES.MONTHS,
  );
  if (!monthsAttribute) return;
  return monthsAttribute.value as number;
}

export function getMembershipInCart(
  cartItemGroups: ParsedCartItemGroup[],
): ParsedCartItem | undefined {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);
  return cartItems.find(
    (item) => (item.productType as ProductType) === ProductType.MEMBERSHIP,
  );
}

export const getFamilySizeFromParsedCartItem = (
  cartItem: ParsedCartItem,
): string | undefined => {
  if (FAMILY_MEMBERSHIP_SKUS.includes(cartItem.sku)) {
    return cartItem.sku[0];
  }

  if (cartItem.attributes) {
    const familySizeAttr = cartItem.attributes.find(
      (attr) =>
        (attr.name as VARIANT_ATTRIBUTE_NAMES) ===
        VARIANT_ATTRIBUTE_NAMES.FAMILY_SIZE,
    );
    if (familySizeAttr?.value) {
      return familySizeAttr.value.toString();
    }
  }

  return undefined;
};

export function cartHasPrepaidMembership(
  cartItemGroups: ParsedCartItemGroup[],
): boolean {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);

  return (
    cartItems.some((item) =>
      [ANNUAL_MEMBERSHIP_KEY, TWENTYFOUR_MONTH_MEMBERSHIP_KEY].includes(
        item.key,
      ),
    ) || cartHasPrepaidMaverickMembership(cartItems)
  );
}

function cartHasPrepaidMaverickMembership(
  cartItems: ParsedCartItem[],
): boolean {
  return cartItems.some((item) => {
    const isPrepaid =
      (item.productType as ProductType) === ProductType.MEMBERSHIP &&
      item.attributes?.some(
        (attr) =>
          (attr.name as VARIANT_ATTRIBUTE_NAMES) ===
            VARIANT_ATTRIBUTE_NAMES.SUBSCRIPTION_TYPE &&
          [PlanTypes.ANNUAL, PlanTypes.TWO_YEAR].includes(attr.value.key),
      );

    const isFamily = item.attributes?.some(
      (attr) =>
        (attr.name as VARIANT_ATTRIBUTE_NAMES) ===
          VARIANT_ATTRIBUTE_NAMES.FAMILY_SIZE && Boolean(attr.value),
    );
    return isPrepaid && !isFamily;
  });
}

export const getCartItemPriceValues = (item: ParsedCartItem) => {
  const isDiscounted = Boolean(item.discountedPrice);
  const originalPrice = item.originalPrice.centAmount;
  let displayPrice;
  if (isDiscounted) {
    displayPrice = item.discountedPrice?.value.centAmount;
  } else {
    displayPrice = originalPrice;
  }
  return { displayPrice, originalPrice, isDiscounted };
};

export function getCartBillingItemAmount(
  billingItems: ParsedBillingItem[],
  type: BillingItemType,
): number | undefined {
  return billingItems.find((detail) => detail.type === type)?.value?.centAmount;
}

export const getPrepaidMembershipInCart = (
  cartItemGroups: ParsedCartItemGroup[],
): ParsedCartItem | undefined => {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);

  return cartItems.find((item) =>
    [ANNUAL_MEMBERSHIP_KEY, TWENTYFOUR_MONTH_MEMBERSHIP_KEY].includes(item.key),
  );
};

export const getPrepaidMembershipFamilyRanges = (
  cartItemGroups: ParsedCartItemGroup[],
) => {
  const prepaidMembership = getPrepaidMembershipInCart(cartItemGroups);
  const prepaidMembershipQty = prepaidMembership?.quantity || 0;
  const hasOnePrepaidMembershipInCart = prepaidMembershipQty === 1;
  const hasMultipleInRangePrepaidMembershipsInCart =
    prepaidMembershipQty > 1 && prepaidMembershipQty <= MAX_FAMILY_SIZE;
  const hasPrepaidMembershipsInRange =
    hasOnePrepaidMembershipInCart || hasMultipleInRangePrepaidMembershipsInCart;
  return {
    hasPrepaidMembershipsInRange,
    hasOnePrepaidMembershipInCart,
    hasMultipleInRangePrepaidMembershipsInCart,
  };
};

export const getPreferredFamilyPlanUpsellInfo = ({
  cartItemGroups,
  annualMembershipProduct,
  familyMembershipProduct,
  isRafGiftFlow,
  asAGift,
  currentPage,
  currency,
  language,
  region,
}: {
  cartItemGroups: ParsedCartItemGroup[];
  annualMembershipProduct?: Product;
  familyMembershipProduct?: Product;
  isRafGiftFlow?: boolean;
  asAGift: boolean;
  currentPage: string;
  currency: Currency;
  language: Language;
  region: Region;
}): {
  displayCTACard: boolean;
  title: string;
  subtitle: string;
} => {
  let title = '',
    subtitle = '';

  // Calculate and format price to add one family member
  const priceToAddOneFamilyMember = getPriceToAddOneFamilyMember({
    displayMembershipProduct: familyMembershipProduct,
    annualMembershipProduct,
  });
  const formattedPriceToAddOneFamilyMember = formatDisplayPriceWithSiteValues(
    priceToAddOneFamilyMember || 0,
    { currency, language, region },
    { roundUp: true },
  );

  const {
    hasPrepaidMembershipsInRange,
    hasOnePrepaidMembershipInCart,
    hasMultipleInRangePrepaidMembershipsInCart,
  } = getPrepaidMembershipFamilyRanges(cartItemGroups);

  // Determine if the CTA card should be displayed
  const displayCTACard = Boolean(
    familyMembershipProduct &&
      !isRafGiftFlow &&
      !asAGift &&
      currentPage !== 'Family' &&
      hasPrepaidMembershipsInRange,
  );

  // Set and return displayCTACard, title, and subtitle
  if (!displayCTACard) return { displayCTACard, title, subtitle };
  if (hasOnePrepaidMembershipInCart) {
    title = i18n.t('cart:includeYourFamily');
    subtitle = i18n.t('cart:saveBigFamilyMembership', {
      price: formattedPriceToAddOneFamilyMember,
    });
  } else if (hasMultipleInRangePrepaidMembershipsInCart) {
    title = i18n.t('cart:buyingMoreThanOne');
    subtitle = i18n.t('cart:saveBigFamilyMoreThanOne');
  }

  return { displayCTACard, title, subtitle };
};

export const getCartItemDescriptionKey = ({
  cartItem,
  region,
  isTrialInCart,
}: {
  cartItem: ParsedCartItem;
  region: Region;
  isTrialInCart: boolean;
}): string | undefined => {
  if (
    shouldExcludeRenewalCopy(region) &&
    cartItem.sku === ANNUAL_MEMBERSHIP_SKU
  )
    return '12MonthMembership';

  if (isTrialInCart && cartItem.sku === GEN_4_DEVICE_SKU) {
    return 'trialNewBand';
  }
};

export function getProductInCart(
  cartItemGroups: ParsedCartItemGroup[],
  productType: ProductType,
): ParsedCartItem | undefined {
  const cartItems = getCartItemsFromCartItemGroups(cartItemGroups);
  return cartItems.find(
    (item) => (item.productType as ProductType) === productType,
  );
}

// This method is only used in MembershipContent, which is only displayed when mav FF is on
// Peak Trial is a weird case where we want to give a user PEAK membership but provide a 4.0/HARVARD device.
// If a user chooses a ONE membership on the OPL page, then a compatible band on the PDP page, they could click trial on the Membership / Plan page
// In such a case, we want getHasTrialIncompatibleBandInCart to return true so we can hide trial card altogether
export const getHasTrialIncompatibleBandInCart = (cart: ParsedCartDTO) => {
  const cartItems = getCartItemsFromCartItemGroups(cart.cartItemGroups);
  return cartItems.some(
    (item) =>
      (item.productType as ProductType) === ProductType.BAND &&
      item.attributes?.some(
        (attr) =>
          (attr.name as VARIANT_ATTRIBUTE_NAMES) ===
            VARIANT_ATTRIBUTE_NAMES.COMPATIBLE_DEVICES &&
          attr.value.some((value) => {
            return [DeviceGeneration.GOOSE, DeviceGeneration.MAVERICK].includes(
              value.key,
            );
          }),
      ),
  );
};

export function isCartHSAEligible(cart: ParsedCartDTO): boolean {
  const cartItems = getCartItemsFromCartItemGroups(cart.cartItemGroups);
  return cartItems.some((item) => {
    const cartItemAttributes = item.attributes;
    if (!cartItemAttributes || cartItemAttributes.length === 0) {
      return false;
    }

    const hsaFsaEligibleAttr = cartItemAttributes.find(
      (attr) => attr.name === 'is-hsa-fsa-eligible',
    );

    return hsaFsaEligibleAttr?.value === true;
  });
}
